import React, { ReactNode } from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "@/utils/cn";

export type TooltipVariant = "default" | "dark" | "light";
export type TooltipSide = "top" | "right" | "bottom" | "left";

export interface TooltipProps {
  content: ReactNode;
  children: ReactNode;
  variant?: TooltipVariant;
  side?: TooltipSide;
  sideOffset?: number;
  className?: string;
  showArrow?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const variantClasses = {
  default: "bg-gray-900 text-white",
  dark: "bg-gray-900 text-white",
  // Theme tokens (not bg-white) so it flips to a dark surface in dark mode --
  // otherwise light-on-dark content (e.g. text-theme-text-heading) renders
  // white on white.
  light: "bg-theme-bg-surface border border-theme-border text-theme-text shadow-lg",
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  variant = "default",
  side = "top",
  sideOffset = 5,
  className = "",
  showArrow = true,
  onOpenChange,
}) => {
  return (
    <TooltipPrimitive.Provider delayDuration={0}>
    <TooltipPrimitive.Root onOpenChange={onOpenChange}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={cn(
            "rounded-md px-2 py-1 text-xs z-50",
            variantClasses[variant],
            variant === "light" && "p-3 max-w-xs",
            className,
          )}
          sideOffset={sideOffset}
          side={side}
        >
          {content}
          {showArrow && (
            <TooltipPrimitive.Arrow
              className={cn(variant === "light" ? "fill-theme-bg-surface" : "fill-gray-900")}
            />
          )}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
};

Tooltip.displayName = "Tooltip";

export default Tooltip;
